/***************************************************************************
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { customElement, property } from 'lit/decorators.js';
import { A3D_TAG_NAMES, A3dElement, toVectorArray as toVectorArrayConverter, } from '../core';
export var A3dCameraErrorNames;
(function (A3dCameraErrorNames) {
    A3dCameraErrorNames["BAD_VECTOR_INPUT"] = "A3dCameraBadVectorInput";
    A3dCameraErrorNames["MISSING_ORBIT"] = "A3dCameraMissingOrbit";
    A3dCameraErrorNames["MISSING_TARGET"] = "A3dCameraMissingTarget";
    A3dCameraErrorNames["ORBIT_CONFLICT"] = "A3dCameraOrbitConflict";
    A3dCameraErrorNames["TARGET_CONFLIT"] = "A3dCameraTargetConflict";
    A3dCameraErrorNames["BAD_NAME"] = "A3dCameraBadCameraName";
})(A3dCameraErrorNames || (A3dCameraErrorNames = {}));
export const A3dCameraErrorMessages = {
    [A3dCameraErrorNames.BAD_VECTOR_INPUT]: '[A3dCamera] Vectors should be defined as "number number number"',
    [A3dCameraErrorNames.MISSING_ORBIT]: 'Warning, you have defined a target without specifying an orbit',
    [A3dCameraErrorNames.MISSING_TARGET]: 'Warning, you have defined an orbit without specifying a target',
    [A3dCameraErrorNames.ORBIT_CONFLICT]: 'Warning, the a3d-viewer attribute camera-orbit is being overridden by the A3dCamera',
    [A3dCameraErrorNames.TARGET_CONFLIT]: 'Warning, the a3d-viewer attribute camera-target is being overridden by the A3dCamera',
    [A3dCameraErrorNames.BAD_NAME]: `Camera with name {{cameraName}} not found`,
};
const toVectorArray = (value) => {
    return toVectorArrayConverter(value, A3dCameraErrorMessages[A3dCameraErrorNames.BAD_VECTOR_INPUT]);
};
/**
 * The `a3d-camera` element defines the camera orbit and target for the default camera of the viewer.
 * It can override the `camera-orbit` and `camera-target` attributes set in the parent `a3d-viewer` element, which are used only on model load.
 */
let A3dCamera = class A3dCamera extends A3dElement {
    constructor() {
        super(...arguments);
        /**
         * The camera orbit is defined by an array of three numbers: [alpha, beta, radius].
         * The alpha and beta values are expressed in radians.
         * @attr orbit - alpha beta radius in radians
         */
        this.orbit = null;
        /**
         * The camera target is defined by an array of three numbers: [x, y, z].
         * The x, y, and z values represent the coordinates in space.
         * @attr target - x y z target point
         */
        this.target = null;
        /**
         * Which model by its index have de camera. Optional.
         * @attr modelIndex - the index of the models in the loadedModels list
         */
        this.modelIndex = null;
        /**
         * Which pre-configured camera to use.
         * @attr cameraName - the camera name to use
         */
        this.cameraName = null;
        this._removeObserver = () => { };
        this._tempVector = Vector3.Zero();
    }
    /**
     * Update viewer default camera with the orbit and target values.
     * For example after moving the camera with the mouse.
     */
    async updateCamera() {
        if (!this._details)
            return;
        if (this.target) {
            this._details.camera.target = this._tempVector.fromArray(this.target);
        }
        if (this.orbit) {
            const properties = ['alpha', 'beta', 'radius'];
            properties.forEach((property, index) => {
                this._details.camera[property] = this.orbit[index];
            });
        }
        if (this.cameraName) {
            const parent = this.parentNode;
            const interpolate = !parent.disableInterpolate;
            const result = this._details.viewer.focusCamera(this.cameraName, this.modelIndex, interpolate);
            if (!result) {
                this._warn(A3dCameraErrorMessages[A3dCameraErrorNames.BAD_NAME].replace('{{cameraName}}', this.cameraName));
                return;
            }
        }
    }
    _observeModelChanged() {
        if (!this._viewer)
            return;
        // apply camera orbit and target when model changed
        this._removeObserver();
        const observer = this._viewer.onModelChanged.add(() => this.updateCamera());
        this._removeObserver = () => this._viewer && this._viewer.onModelChanged.remove(observer);
    }
    _checkConflicts() {
        if (this.parentElement?.tagName == A3D_TAG_NAMES.VIEWER) {
            if (this.parentElement.getAttribute('camera-orbit') !== null) {
                this._warn(A3dCameraErrorMessages[A3dCameraErrorNames.ORBIT_CONFLICT]);
            }
            if (this.parentElement.getAttribute('camera-target') !== null) {
                this._warn(A3dCameraErrorMessages[A3dCameraErrorNames.TARGET_CONFLIT]);
            }
        }
    }
    /**
     * @internal
     */
    async _initElement() {
        this._checkConflicts();
        this._observeModelChanged();
        this.updateCamera();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this._removeObserver();
    }
    update(changedProperties) {
        super.update(changedProperties);
        this._validateProperties();
        this.updateCamera();
    }
    _validateProperties() {
        let isValid = true;
        if (this.orbit !== null && this.target === null) {
            this._warn(A3dCameraErrorMessages[A3dCameraErrorNames.MISSING_TARGET]);
        }
        if (this.orbit === null && this.target !== null) {
            this._warn(A3dCameraErrorMessages[A3dCameraErrorNames.MISSING_ORBIT]);
        }
        return isValid;
    }
};
__decorate([
    property({ converter: toVectorArray })
], A3dCamera.prototype, "orbit", void 0);
__decorate([
    property({ converter: toVectorArray })
], A3dCamera.prototype, "target", void 0);
__decorate([
    property({ type: Number, attribute: 'model-index' })
], A3dCamera.prototype, "modelIndex", void 0);
__decorate([
    property({ type: String, attribute: 'camera-name' })
], A3dCamera.prototype, "cameraName", void 0);
A3dCamera = __decorate([
    customElement('a3d-camera')
], A3dCamera);
export { A3dCamera };
